/**
 * Settings — hub matching the iOS SettingsScreen structure.
 * Web port of client-native/src/features/settings/screens/SettingsScreen.tsx
 * and its sub-screens.
 *
 * Navigation mirrors iOS: each hub row `router.push`es a dedicated sub-screen
 * (Profile / Feed / Sound / Activities / Tutorials) with a back chevron +
 * centered title; the Parrot Method banner pushes the method carousel. Web
 * reproduces that as in-place pushed views (a `view` state + slide-in) rather
 * than the prior inline accordion, so each sub-screen reads as its own screen.
 *
 * Sections: Parrot Method banner → Profile / Feed Settings / Sound / Activities
 * / Tutorials / Retake Placement Test → FOLLOW PARROT → ACCOUNT (Subscription /
 * Billing / Delete Account / Log Out) → Help Links → Version.
 */
import React, { FC, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useToast } from "@chakra-ui/react";
import { ChevronRight, MessageCircleMore } from "lucide-react";
import BackButton from "@/ui/BackButton";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useFeatureFlagEnabled } from "posthog-js/react";
import { colors, FlagIcon } from "@/ui";
import {
  callWithAuth,
  deleteUser,
  postCreateCustomerPortalSession,
  getUserInfo,
} from "@/api/Api";
import { logoutAndClearUserState, clearAllUserStateForDeletion } from "@/lib/logout";
import VERSION, { DEV_ADDON } from "@/version";
import MethodExplanationPage from "@/pages/MethodExplanationPage";
import DeleteAccountModal from "./settings/DeleteAccountModal";
import DialectFlag from "./settings/DialectFlag";
import SettingsSubPage from "./settings/SettingsSubPage";
import FeedSettingsSection from "./settings/FeedSettingsSection";
import ProfileSettingsSection from "./settings/ProfileSettingsSection";
import SoundSettingsSection from "./settings/SoundSettingsSection";
import ActivitiesSettingsSection from "./settings/ActivitiesSettingsSection";
import TutorialsSettingsSection from "./settings/TutorialsSettingsSection";
import { AppDownloadSettingsRow } from "../AppDownloadPrompt";

interface SettingsProps {
  onClose: () => void;
  userEmail: string;
  onTrackDeleteAccountClicked: () => void;
  onTrackManageBillingClicked: () => void;
  onTrackLogoutClicked?: () => void;
}

const SOCIAL_LINKS = [
  { emoji: "📸", label: "Instagram", url: "https://www.instagram.com/getparrot/" },
  { emoji: "🎵", label: "TikTok", url: "https://www.tiktok.com/@parrotapp" },
  { emoji: "💼", label: "LinkedIn", url: "https://www.linkedin.com/company/104851379/" },
  { emoji: "▶️", label: "YouTube", url: "https://www.youtube.com/@ParrotApp" },
];

/** Pushed sub-screens (excludes the Parrot Method carousel, which has its own
 *  full-screen layout). */
type SubScreen = "profile" | "feed" | "sound" | "activities" | "tutorials";
type SettingsView = SubScreen | "method" | null;

const SUB_TITLES: Record<SubScreen, string> = {
  profile: "Profile",
  feed: "Feed Settings",
  sound: "Sound",
  activities: "Activities",
  tutorials: "Tutorials",
};

// iOS renderSectionLabel (SettingsScreen.tsx:192-201): variant="small",
// weight semibold, color Secondary, uppercase, letterSpacing 0.5, marginTop 24,
// marginBottom 8.
const SectionLabel: React.FC<{ children: string }> = ({ children }) => (
  <div
    className="text-sm font-semibold"
    style={{
      width: "100%",
      textAlign: "left",
      color: colors.secondary,
      letterSpacing: 0.5,
      textTransform: "uppercase",
      marginTop: 24,
      marginBottom: 8,
    }}
  >
    {children}
  </div>
);

const Settings: React.FC<SettingsProps> = ({
  onClose,
  userEmail,
  onTrackDeleteAccountClicked,
  onTrackManageBillingClicked,
  onTrackLogoutClicked,
}) => {
  const nav = useNavigate();
  const queryClient = useQueryClient();
  const toast = useToast();
  // iOS gates the SUPPORT row + chat FAB behind the chat_with_us_enabled flag.
  const chatEnabled = useFeatureFlagEnabled("chat_with_us_enabled");

  const [view, setView] = useState<SettingsView>(null);
  const [deleteOpen, setDeleteOpen] = useState(false);

  const userInfoQuery = useQuery({
    queryKey: ["userInfo"],
    queryFn: () => callWithAuth(getUserInfo),
  });
  const user = userInfoQuery.data;

  // ----- Delete account (mirrors iOS DeleteAccountBottomSheet onAuth/onDelete) -----
  const handleDeleteAuth = (typed: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (typed.trim() === "") throw new Error("Email required");
    if (!emailRegex.test(typed)) throw new Error("Invalid email");
    if (typed.trim().toLowerCase() !== (userEmail || "").toLowerCase())
      throw new Error("Entered email does not match user email");
  };
  const handleDelete = async () => {
    await callWithAuth(deleteUser); // signs out (clears token) server + client
    queryClient.clear();
    // The account is gone — wipe ALL local per-user state (avatar, course,
    // onboarding flags, etc.). queryClient.clear() only drops the query cache;
    // without this the deleted user's localStorage lingered into the next session.
    clearAllUserStateForDeletion();
    nav("/");
  };

  // ----- Log out (mirrors iOS handleLogout: clear + navigate, no confirm) -----
  const handleLogout = () => {
    onTrackLogoutClicked?.();
    try {
      logoutAndClearUserState();
      nav("/");
    } catch (err) {
      console.error("[Settings] logout failed:", err);
    }
  };

  const manageBilling = async () => {
    onTrackManageBillingClicked();
    const response = await callWithAuth(postCreateCustomerPortalSession);
    window.location.href = response.url;
  };

  // Subscription-row routing. "No stripeCustomerId" does NOT mean "free" — most
  // paid users are IAP (Apple/Google), and partner/admin/comp roles have access
  // without ever paying through us. Gate the upgrade path on the server's
  // `isFree` flag (role === free) rather than inferring from paid flags:
  //   - Stripe customer → hosted customer portal (manage/cancel/invoices)
  //   - isFree          → /upgrade (existing signed-in user; NOT the /premium
  //                       signup-checkout path)
  //   - anyone else w/o Stripe (premium/trial/temp_*/partner/admin/comp) →
  //                       managed outside the app (IAP or a grant); just inform.
  // Guarded on loading so a Stripe subscriber isn't misrouted before user info
  // resolves; `void` keeps the click handler synchronous (no floating promise).
  const handleSubscription = () => {
    if (userInfoQuery.isLoading || !user) return;
    if (user.stripeCustomerId) {
      void manageBilling();
      return;
    }
    if (user.isFree) {
      nav("/upgrade");
      return;
    }
    toast({
      title: "Manage your subscription",
      description:
        "Your subscription is managed outside the app. If you subscribed on iOS or Android, manage or cancel it in the App Store or Google Play.",
      status: "info",
      duration: 6000,
      isClosable: true,
      position: "top",
    });
  };

  const cardStyle: React.CSSProperties = {
    backgroundColor: colors.white,
    borderRadius: 20,
    boxShadow: "var(--shadow-card-border)",
    overflow: "hidden",
  };
  const rowDivider = <div style={{ height: 1, backgroundColor: colors.greyLightest }} />;

  // ── Pushed sub-screens (iOS router.push). ──
  // The Parrot Method carousel has its own full-screen layout (header, dots,
  // bottom button), so it renders standalone with a back chevron + "Got it!".
  if (view === "method") {
    return (
      <MethodExplanationPage
        finalButtonText="Got it!"
        showBackButton
        onBack={() => setView(null)}
        onComplete={() => setView(null)}
      />
    );
  }

  if (view) {
    const body =
      view === "profile" ? (
        <ProfileSettingsSection
          firstName={user?.firstName ?? ""}
          email={user?.email ?? userEmail}
        />
      ) : view === "feed" ? (
        <FeedSettingsSection />
      ) : view === "sound" ? (
        <SoundSettingsSection />
      ) : view === "activities" ? (
        <ActivitiesSettingsSection />
      ) : (
        <TutorialsSettingsSection />
      );

    return (
      <div key={view} className="settings-push-in">
        <SettingsSubPage title={SUB_TITLES[view]} onBack={() => setView(null)}>
          {body}
        </SettingsSubPage>
        {/* Slide-in to evoke the iOS push (kept subtle so it never overflows the
            centered column horizontally). */}
        <style>{`
          @keyframes settings-push-in {
            from { transform: translateX(40px); opacity: 0; }
            to { transform: translateX(0); opacity: 1; }
          }
          .settings-push-in { animation: settings-push-in 220ms ease-out; }
        `}</style>
      </div>
    );
  }

  // Reusable hub row that pushes a sub-screen (trailing › chevron, iOS MenuItem).
  const PushRow: FC<{ row: SubScreen; emoji: string; label: string }> = ({
    row,
    emoji,
    label,
  }) => (
    <button
      type="button"
      onClick={() => setView(row)}
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        width: "100%",
        padding: "14px 16px",
        background: "none",
        border: "none",
        cursor: "pointer",
      }}
    >
      <span style={{ display: "flex", alignItems: "center", gap: 12 }}>
        <span style={{ width: 24, textAlign: "center", fontSize: 18 }}>{emoji}</span>
        <span className="text-base" style={{ color: colors.primary }}>{label}</span>
      </span>
      <ChevronRight size={18} color={colors.muted} />
    </button>
  );

  return (
    <div className="flex flex-col w-full gap-3 text-base">
      {/* Header — mirrors iOS SettingsScreen: back chevron on the left, a
          horizontally-centered 'Settings' title, and an empty right spacer. */}
      <div className="flex flex-row w-full items-center">
        <BackButton onPress={onClose} color={colors.primary} />
        <span className="text-lg font-bold flex-1 text-center" style={{ color: colors.primary }}>
          Settings
        </span>
        <span style={{ width: 32 }} />
      </div>

      {/* Parrot Method banner → pushes the method carousel */}
      <button
        type="button"
        onClick={() => setView("method")}
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          width: "100%",
          backgroundColor: colors.blue,
          borderRadius: 32,
          padding: "16px 20px",
          border: "none",
          cursor: "pointer",
        }}
      >
        <span style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{ display: "flex", gap: 2, alignItems: "center" }}>
            <FlagIcon countryCode="ES" size="small" />
            <DialectFlag kind="latam" size="small" />
          </span>
          <span className="text-base font-semibold" style={{ color: colors.white }}>
            How to Speak Español Effectively
          </span>
        </span>
        <span className="text-lg" style={{ color: colors.white }}>›</span>
      </button>

      {/* "Get the iOS app" row — high up, just under the method banner (iOS-web
          only; renders nothing otherwise). */}
      <AppDownloadSettingsRow />

      {/* Main menu card */}
      <div style={cardStyle}>
        <PushRow row="profile" emoji="👤" label="Profile" />
        {rowDivider}
        <PushRow row="feed" emoji="📺" label="Feed Settings" />
        {rowDivider}
        <PushRow row="sound" emoji="🔊" label="Sound" />
        {rowDivider}
        <PushRow row="activities" emoji="🎯" label="Activities" />
        {rowDivider}
        <PushRow row="tutorials" emoji="📖" label="Tutorials" />
        {rowDivider}
        <button
          type="button"
          onClick={() => nav("/placement?mode=redo")}
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            width: "100%",
            padding: "14px 16px",
            background: "none",
            border: "none",
            cursor: "pointer",
          }}
        >
          <span style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <span style={{ width: 24, textAlign: "center", fontSize: 18 }}>🔄</span>
            <span className="text-base" style={{ color: colors.primary }}>Retake Placement Test</span>
          </span>
          <ChevronRight size={18} color={colors.muted} />
        </button>
      </div>

      {/* Support — gated behind chat_with_us_enabled. */}
      {chatEnabled && (
        <>
          <SectionLabel>SUPPORT</SectionLabel>
          <div style={cardStyle}>
            <button
              type="button"
              onClick={() => nav("/conversation")}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 12,
                width: "100%",
                padding: "14px 16px",
                background: "none",
                border: "none",
                cursor: "pointer",
              }}
            >
              <span style={{ width: 24, textAlign: "center", fontSize: 18 }}>💬</span>
              <span className="text-base" style={{ color: colors.primary }}>Chat with the Founders</span>
            </button>
          </div>
        </>
      )}

      {/* Follow Parrot */}
      <SectionLabel>FOLLOW PARROT</SectionLabel>
      <div style={cardStyle}>
        {SOCIAL_LINKS.map((link, i) => (
          <React.Fragment key={link.label}>
            {i > 0 && rowDivider}
            <a
              href={link.url}
              target="_blank"
              rel="noreferrer"
              style={{
                display: "flex",
                alignItems: "center",
                gap: 12,
                padding: "14px 16px",
                textDecoration: "none",
              }}
            >
              <span style={{ width: 24, textAlign: "center", fontSize: 18 }}>{link.emoji}</span>
              <span className="text-base" style={{ color: colors.primary }}>{link.label}</span>
            </a>
          </React.Fragment>
        ))}
      </div>

      {/* Account */}
      <SectionLabel>ACCOUNT</SectionLabel>
      <div style={cardStyle}>
        {/* Subscription — always visible (web keeps this even though iOS #755
            dropped it). Routing handled in `handleSubscription`: Stripe customers
            → portal; IAP subscribers → store-manage message; free → /upgrade.
            Subsumes the old stripe-only "Billing" row. */}
        <button
          type="button"
          onClick={handleSubscription}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 12,
            width: "100%",
            padding: "14px 16px",
            background: "none",
            border: "none",
            cursor: "pointer",
          }}
        >
          <span style={{ width: 24, textAlign: "center", fontSize: 18 }}>💳</span>
          <span className="text-base" style={{ color: colors.primary }}>Subscription</span>
        </button>
        {rowDivider}
        <button
          type="button"
          onClick={() => setDeleteOpen(true)}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 12,
            width: "100%",
            padding: "14px 16px",
            background: "none",
            border: "none",
            cursor: "pointer",
          }}
        >
          <span style={{ width: 24, textAlign: "center", fontSize: 18 }}>🗑️</span>
          <span className="text-base" style={{ color: colors.primary }}>Delete Account</span>
        </button>
        {rowDivider}
        <button
          type="button"
          onClick={handleLogout}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 12,
            width: "100%",
            padding: "14px 16px",
            background: "none",
            border: "none",
            cursor: "pointer",
          }}
        >
          <span style={{ width: 24, textAlign: "center", fontSize: 18 }}>👋</span>
          <span className="text-base" style={{ color: colors.error }}>Log Out</span>
        </button>
      </div>

      {/* Help Links */}
      <div className="text-xs w-full flex flex-row justify-center items-center" style={{ color: colors.secondary, marginTop: 8 }}>
        <a style={{ cursor: "pointer" }} onClick={() => nav("/privacy")}>Privacy Policy</a>
        <span className="mx-2">|</span>
        <a style={{ cursor: "pointer" }} onClick={() => nav("/terms")}>Terms of Service</a>
        <span className="mx-2">|</span>
        <a style={{ cursor: "pointer" }} onClick={() => nav("/support")}>Support</a>
      </div>
      <div className="text-xs" style={{ color: colors.secondary, textAlign: "center" }}>
        {VERSION} {DEV_ADDON}
      </div>

      {/* Floating chat FAB — gated behind chat_with_us_enabled. Pinned to the
          bottom-right of the centered max-w-[600px] column, not the viewport. */}
      {chatEnabled && (
        <div
          style={{
            position: "fixed",
            left: 0,
            right: 0,
            bottom: 88,
            display: "flex",
            justifyContent: "center",
            pointerEvents: "none",
            zIndex: 50,
          }}
        >
          <div
            style={{
              position: "relative",
              width: "100%",
              maxWidth: 600,
              boxSizing: "border-box",
              paddingRight: 20,
            }}
          >
            <button
              type="button"
              aria-label="Chat with the Founders"
              onClick={() => nav("/conversation")}
              style={{
                position: "absolute",
                right: 20,
                bottom: 0,
                width: 56,
                height: 56,
                borderRadius: 28,
                backgroundColor: colors.blue,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                border: "none",
                cursor: "pointer",
                boxShadow: "0 4px 8px rgba(0,0,0,0.25)",
                pointerEvents: "auto",
              }}
            >
              {/* Matches iOS chat FAB (Ionicons "chatbubble-ellipses": a filled
                  bubble with three dots) rather than lucide's plain MessageCircle. */}
              <MessageCircleMore size={24} color={colors.white} />
            </button>
          </div>
        </div>
      )}

      <DeleteAccountModal
        open={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        userEmail={user?.email ?? userEmail}
        onAuth={handleDeleteAuth}
        onDelete={handleDelete}
        onTrackDeleteAccountClicked={onTrackDeleteAccountClicked}
      />
    </div>
  );
};

export default Settings;
